/**
 * pages/AdminUsers.tsx
 *
 * Admin-only page listing all non-admin users with their form counts.
 * Shows name, email, registration date, form count, and invitation status.
 * Admin can invite new users by email.
 * Created: 2026-03-11
 * Updated: 2026-03-12 - display name and initials now use first_name/last_name
 *   with silent email-prefix fallback for legacy users without names set.
 * Updated: 2026-03-25 - added invitation status badge, invite button, resend action.
 *   Helper functions consolidated into useUsersList to avoid duplication.
 * Updated: 2026-03-25 - added 'expired' derived status (pending > 24h), contextual
 *   resend button styling, and invited_at time display under status badge.
 * Updated: 2026-03-25 - extracted invite expiry threshold into INVITE_LINK_TTL_HOURS constant
 *   to document its dependency on MAILER_OTP_EXP (Supabase Auth internal setting).
 * Updated: 2026-04-14 - removed redundant invited_at display from UserStatusCell.
 *   Replaced resend modal flow with inline UserActionsDropdown (generate/email/copy).
 * Updated: 2026-04-14 - dropdown now shown for all users (not just pending);
 *   added userId prop to UserActionsDropdown to enable user deletion.
 */

import { useEffect, useState, useMemo } from 'react';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../lib/supabase';
import { captureError } from '../utils/errorReporting';
import AppLayout from '../components/layout/AppLayout';
import Card from '../components/ui/Card';
import Spinner from '../components/ui/Spinner';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import InviteUserModal from '../components/admin/InviteUserModal';
import UserActionsDropdown from '../components/admin/UserActionsDropdown';
import { useSortableTable } from '../hooks/useSortableTable';
import {
  getUserDisplayName,
  getUserInitials,
  getColorFromEmail,
} from '../hooks/useUsersList';
import { Users, Search, SearchX, FileText, UserPlus } from 'lucide-react';
import { formatRelativeDate } from '../utils/dateUtils';

type InvitationStatus = 'pending' | 'accepted' | null;
type DisplayStatus = 'active' | 'pending' | 'expired';

interface UserRow {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  created_at: string;
  form_count: number;
  invitation_status: InvitationStatus;
  invited_at: string | null;
}

// Mirrors Supabase Auth's MAILER_OTP_EXP (default 3600 s, max 86400 s = 24 h).
// Update this value if MAILER_OTP_EXP is customised via the Supabase Management API.
const INVITE_LINK_TTL_HOURS = 24;

function getDisplayStatus(u: UserRow): DisplayStatus {
  if (u.invitation_status !== 'pending') return 'active';
  if (!u.invited_at) return 'pending';
  const hoursSince = (Date.now() - new Date(u.invited_at).getTime()) / (1000 * 60 * 60);
  return hoursSince >= INVITE_LINK_TTL_HOURS ? 'expired' : 'pending';
}

function UserStatusCell({ u }: { u: UserRow }) {
  const status = getDisplayStatus(u);
  return (
    <div className="flex flex-col gap-1">
      {status === 'active' && <Badge variant="success" size="sm">Actif</Badge>}
      {status === 'pending' && <Badge variant="warning" size="sm">En attente</Badge>}
      {status === 'expired' && <Badge variant="error" size="sm">Expiré</Badge>}
    </div>
  );
}

export default function AdminUsers() {
  const { showToast } = useToast();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, email, first_name, last_name, created_at, invitation_status, invited_at')
        .eq('is_admin', false)
        .order('created_at', { ascending: false });

      if (usersError) throw usersError;
      if (!usersData || usersData.length === 0) {
        setUsers([]);
        return;
      }

      const userIds = usersData.map((u) => u.id);

      const { data: templatesData, error: templatesError } = await supabase
        .from('form_templates')
        .select('user_id')
        .in('user_id', userIds);

      if (templatesError) throw templatesError;

      const countMap = new Map<string, number>();
      (templatesData || []).forEach((t) => {
        countMap.set(t.user_id, (countMap.get(t.user_id) || 0) + 1);
      });

      setUsers(
        usersData.map((u) => ({
          id: u.id,
          email: u.email,
          first_name: u.first_name,
          last_name: u.last_name,
          created_at: u.created_at,
          form_count: countMap.get(u.id) || 0,
          invitation_status: (u.invitation_status as InvitationStatus) ?? null,
          invited_at: u.invited_at ?? null,
        }))
      );
    } catch (err) {
      console.error('Error fetching users:', err);
      captureError(err, { page: 'AdminUsers', action: 'fetchUsers' });
      showToast('Erreur lors du chargement des utilisateurs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ').toLowerCase();
      return u.email.toLowerCase().includes(q) || fullName.includes(q);
    });
  }, [users, searchQuery]);

  const { sortedData, toggleSort, renderSortIcon } = useSortableTable({
    data: filtered,
    initialSortKey: 'created_at',
    initialSortDirection: 'desc',
  });

  const thBtn =
    'flex items-center gap-1.5 text-xs font-semibold text-neutral-500 uppercase tracking-wider hover:text-neutral-700 transition-colors';

  const inviteButton = (
    <Button variant="primary" onClick={() => setIsInviteOpen(true)}>
      <UserPlus className="w-4 h-4 mr-1.5" />
      Inviter un utilisateur
    </Button>
  );

  return (
    <AppLayout
      breadcrumbs={[
        { label: 'Qollect', path: '/dashboard' },
        { label: 'Administration' },
        { label: 'Utilisateurs' },
      ]}
      actions={
        <div className="flex items-center gap-3 w-full sm:w-auto">
          {users.length > 0 && (
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-600 focus:border-transparent bg-white shadow-soft"
              />
            </div>
          )}
          {inviteButton}
        </div>
      }
    >
      {loading ? (
        <Card>
          <div className="text-center py-16">
            <div className="text-primary-600 mb-4 flex justify-center">
              <Spinner size="md" />
            </div>
            <p className="text-neutral-500">Chargement...</p>
          </div>
        </Card>
      ) : users.length === 0 ? (
        <Card>
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-neutral-100 rounded-3xl mb-5">
              <Users className="w-10 h-10 text-neutral-400" />
            </div>
            <p className="text-neutral-600 mb-2 text-lg font-semibold">Aucun utilisateur</p>
            <p className="text-sm text-neutral-400 max-w-sm mx-auto mb-6">
              Invitez votre premier utilisateur pour qu'il puisse accéder à la plateforme.
            </p>
            {inviteButton}
          </div>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-100 rounded-2xl mb-4">
              <SearchX className="w-8 h-8 text-neutral-400" />
            </div>
            <p className="text-neutral-600 text-lg font-medium mb-2">Aucun résultat</p>
            <p className="text-sm text-neutral-500 mb-6">
              Aucun utilisateur ne correspond à "{searchQuery}"
            </p>
            <Button variant="secondary" onClick={() => setSearchQuery('')}>
              Effacer la recherche
            </Button>
          </div>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-neutral-200 bg-white shadow-soft">
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="border-b border-neutral-100 bg-neutral-50/60">
                <th className="text-left px-5 py-3.5">
                  <button onClick={() => toggleSort('email')} className={thBtn}>
                    Utilisateur {renderSortIcon('email')}
                  </button>
                </th>
                <th className="text-left px-4 py-3.5 hidden sm:table-cell">
                  <span className={thBtn}>Statut</span>
                </th>
                <th className="text-left px-4 py-3.5 hidden sm:table-cell">
                  <button onClick={() => toggleSort('form_count')} className={thBtn}>
                    Formulaires {renderSortIcon('form_count')}
                  </button>
                </th>
                <th className="text-left px-4 py-3.5 hidden md:table-cell">
                  <button onClick={() => toggleSort('created_at')} className={thBtn}>
                    Inscrit {renderSortIcon('created_at')}
                  </button>
                </th>
                <th className="px-4 py-3.5 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-50">
              {sortedData.map((u) => {
                const displayStatus = getDisplayStatus(u);
                return (
                  <tr key={u.id} className="hover:bg-neutral-50/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-full ${getColorFromEmail(u.email)} flex items-center justify-center shrink-0`}
                        >
                          <span className="text-xs font-semibold text-white">
                            {getUserInitials(u)}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-neutral-900 truncate">
                            {getUserDisplayName(u)}
                          </p>
                          <p className="text-xs text-neutral-400 truncate">{u.email}</p>
                        </div>
                        {u.invitation_status === 'pending' && (
                          <Badge
                            variant={displayStatus === 'expired' ? 'error' : 'warning'}
                            size="sm"
                            className="sm:hidden shrink-0"
                          >
                            {displayStatus === 'expired' ? 'Expiré' : 'En attente'}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell">
                      <UserStatusCell u={u} />
                    </td>
                    <td className="px-4 py-4 hidden sm:table-cell">
                      <div className="flex items-center gap-1.5 text-neutral-600">
                        <FileText className="w-3.5 h-3.5 text-neutral-400" />
                        <span className="text-sm">{u.form_count}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell text-neutral-500 text-xs whitespace-nowrap">
                      {formatRelativeDate(u.created_at)}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <UserActionsDropdown
                        userId={u.id}
                        email={u.email}
                        displayStatus={displayStatus}
                        onActionComplete={fetchUsers}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <InviteUserModal
        isOpen={isInviteOpen}
        onClose={() => setIsInviteOpen(false)}
        onSuccess={fetchUsers}
      />
    </AppLayout>
  );
}
